import { forwardRef, useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { intakeFormSchema, type IntakeFormValues } from '../domain/schemas';
import { useAppState } from '../state/AppState';
import { BRANCHES, SOURCES_OF_FUNDS, type ClientRecord, type KycStatus } from '../domain/types';
import { COUNTRIES } from '../domain/countries';
import { classify } from '../domain/rules';
import { putClient } from '../persistence/db';
import { recordAudit } from '../persistence/audit';
import { RiskBadge } from './RiskBadge';
import styles from './IntakeForm.module.css';

function newClientId(): string {
  const today = new Date().toISOString().slice(0, 10).replaceAll('-', '');
  const suffix =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `CLT-${today}-${suffix}`.toUpperCase();
}

function kycStatusFor(tier: 'LOW' | 'MEDIUM' | 'HIGH'): KycStatus {
  if (tier === 'HIGH') return 'ENHANCED_DUE_DILIGENCE';
  return 'PENDING';
}

interface CheckProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

const Check = forwardRef<HTMLInputElement, CheckProps>(({ label, ...rest }, ref) => (
  <label className={styles.check}>
    <input ref={ref} type="checkbox" className={styles.checkInput} {...rest} />
    <span className={styles.checkBox} aria-hidden="true" />
    <span className={styles.checkLabel}>{label}</span>
  </label>
));
Check.displayName = 'Check';

export function IntakeForm() {
  const { state, setView, addClient } = useAppState();
  const rules = state.rules;
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const rmDisplay = state.currentRm ?? '';
  const rmName = rmDisplay.split(' · ')[0] ?? rmDisplay;
  const rmBranchFromSession = rmDisplay.split(' · ')[1];
  const defaultBranch =
    rmBranchFromSession && (BRANCHES as readonly string[]).includes(rmBranchFromSession)
      ? rmBranchFromSession
      : BRANCHES[0];

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<IntakeFormValues>({
    resolver: zodResolver(intakeFormSchema),
    mode: 'onChange',
    defaultValues: {
      client_name: '',
      client_type: 'INDIVIDUAL',
      branch: defaultBranch,
      country_of_tax_residence: '',
      annual_income: 0,
      source_of_funds: 'Employment',
      pep_status: false,
      sanctions_screening_match: false,
      adverse_media_flag: false,
      documentation_complete: false,
      id_verification_date: new Date().toISOString().slice(0, 10),
      relationship_manager: rmName,
    },
  });

  const watched = watch();

  const liveClassification = useMemo(() => {
    if (!rules) return null;
    return classify(
      {
        pep_status: !!watched.pep_status,
        sanctions_screening_match: !!watched.sanctions_screening_match,
        adverse_media_flag: !!watched.adverse_media_flag,
        country_of_tax_residence: watched.country_of_tax_residence || '',
        client_type: watched.client_type,
        annual_income: Number(watched.annual_income) || 0,
        source_of_funds: watched.source_of_funds || '',
      },
      rules,
    );
  }, [rules, watched]);

  const onSubmit: SubmitHandler<IntakeFormValues> = async (values) => {
    if (!rules) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const classification = classify(values, rules);
      const record: ClientRecord = {
        client_id: newClientId(),
        branch: values.branch,
        onboarding_date: new Date().toISOString().slice(0, 10),
        client_name: values.client_name.trim(),
        client_type: values.client_type,
        country_of_tax_residence: values.country_of_tax_residence,
        annual_income: values.annual_income,
        source_of_funds: values.source_of_funds,
        pep_status: values.pep_status,
        sanctions_screening_match: values.sanctions_screening_match,
        adverse_media_flag: values.adverse_media_flag,
        risk_classification: classification.tier,
        kyc_status: kycStatusFor(classification.tier),
        id_verification_date: values.id_verification_date,
        relationship_manager: values.relationship_manager,
        documentation_complete: values.documentation_complete,
      };

      await putClient(record);
      await recordAudit({
        action: 'INTAKE_CREATED',
        actor: state.currentRm ?? 'unknown',
        record,
        classification,
      });
      await addClient(record);

      reset();
      setView({ kind: 'detail', clientId: record.client_id });
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Failed to save the assessment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.wrap}>
      <header className={styles.header}>
        <h1 className={styles.title}>New client assessment</h1>
        <p className={styles.subtitle}>
          Risk tier is computed by the system from regulatory criteria. You cannot override it.
        </p>
      </header>

      <form
        className={styles.layout}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        aria-describedby={serverError ? 'intake-server-error' : undefined}
      >
        <div className={styles.formCol}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Client</h2>

            <div className={styles.row}>
              <Field label="Client name" error={errors.client_name?.message}>
                <input
                  className={styles.input}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="words"
                  maxLength={120}
                  aria-invalid={!!errors.client_name}
                  {...register('client_name')}
                />
              </Field>
            </div>

            <div className={styles.row}>
              <Field label="Client type" error={errors.client_type?.message}>
                <div className={styles.segmented} role="radiogroup" aria-label="Client type">
                  <label className={styles.segment}>
                    <input
                      type="radio"
                      value="INDIVIDUAL"
                      {...register('client_type')}
                      className={styles.segmentInput}
                    />
                    <span>Individual</span>
                  </label>
                  <label className={styles.segment}>
                    <input
                      type="radio"
                      value="ENTITY"
                      {...register('client_type')}
                      className={styles.segmentInput}
                    />
                    <span>Entity</span>
                  </label>
                </div>
              </Field>

              <Field label="Branch" error={errors.branch?.message}>
                <select className={styles.input} {...register('branch')}>
                  {BRANCHES.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className={styles.row}>
              <Field
                label="Country of tax residence"
                error={errors.country_of_tax_residence?.message}
              >
                <select className={styles.input} {...register('country_of_tax_residence')}>
                  <option value="">Select…</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Wealth profile</h2>

            <div className={styles.row}>
              <Field
                label="Annual income (GBP)"
                error={errors.annual_income?.message}
                hint="Or annual revenue for entities."
              >
                <input
                  className={styles.input}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  step={1000}
                  aria-invalid={!!errors.annual_income}
                  {...register('annual_income', { valueAsNumber: true })}
                />
              </Field>

              <Field label="Source of funds" error={errors.source_of_funds?.message}>
                <select className={styles.input} {...register('source_of_funds')}>
                  {SOURCES_OF_FUNDS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Screening</h2>
            <div className={styles.checkRow}>
              <Check label="Politically Exposed Person (PEP)" {...register('pep_status')} />
              <Check
                label="HMT/OFSI sanctions screening match"
                {...register('sanctions_screening_match')}
              />
              <Check label="Adverse media flag" {...register('adverse_media_flag')} />
            </div>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Documentation</h2>

            <div className={styles.row}>
              <Field
                label="ID verification date"
                error={errors.id_verification_date?.message}
              >
                <input
                  className={styles.input}
                  type="date"
                  max={new Date().toISOString().slice(0, 10)}
                  aria-invalid={!!errors.id_verification_date}
                  {...register('id_verification_date')}
                />
              </Field>

              <Field label="Relationship manager" error={errors.relationship_manager?.message}>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={64}
                  {...register('relationship_manager')}
                  readOnly
                />
              </Field>
            </div>

            <div className={styles.checkRow}>
              <Check
                label="All required documents collected"
                {...register('documentation_complete')}
              />
            </div>
          </div>
        </div>

        <aside className={styles.summaryCol} aria-label="Live risk classification">
          <div className={styles.summary}>
            <h2 className={styles.summaryTitle}>Live risk classification</h2>
            <p className={styles.summaryLead}>
              Updates as you fill the form. This is the tier that will be recorded.
            </p>

            {liveClassification && (
              <div className={styles.summaryBody}>
                <RiskBadge tier={liveClassification.tier} size="lg" />
                <div className={styles.reasonsTitle}>Why</div>
                <ul className={styles.reasons}>
                  {liveClassification.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
                <div className={styles.rulesVersion}>
                  Rules version: {liveClassification.rulesVersion}
                </div>
              </div>
            )}

            {liveClassification?.tier === 'HIGH' && (
              <div className={styles.eddNotice} role="status">
                HIGH-risk client. KYC will be saved as ENHANCED_DUE_DILIGENCE and cannot be
                approved without senior compliance sign-off.
              </div>
            )}

            {serverError && (
              <div id="intake-server-error" className={styles.serverError} role="alert">
                {serverError}
              </div>
            )}

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setView({ kind: 'list' })}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={submitting || !isValid}
              >
                {submitting ? 'Saving…' : 'Save assessment'}
              </button>
            </div>
          </div>
        </aside>
      </form>
    </section>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      {children}
      {hint && !error && <span className={styles.hint}>{hint}</span>}
      {error && (
        <span className={styles.errorText} role="alert">
          {error}
        </span>
      )}
    </label>
  );
}
